// ============================================================================
// CardShell — shared chrome for AM Co-pilot proposal cards.
// Renders the title row (icon + tool label + Discard link), the children
// editor body, and a "Do it →" / committed-state footer pinned to the bottom
// of the card (sticky inside the chat's scroll area, so it stays in reach).
//
// 2026-10-21 (Ask AI phone basics): every control in a card is at least 44 px
// tall, and once a card is committed or discarded its fields are LOCKED — a
// <fieldset disabled> — so nothing looks saveable that is not.
//
// 2026-05-18 (Phase 2): when a card transitions to Committed /
// Edited_And_Committed, auto-collapse the body to a one-line summary after
// COLLAPSE_DELAY_MS. The user can click the chevron to re-expand. Discarded
// cards stay visible (faded) until startNewChat clears the conversation.
// ============================================================================

import { useEffect, useState, type ReactNode } from 'react';
import { Check, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import type { CopilotProposalStatus } from '../../../shared/types';

const COLLAPSE_DELAY_MS = 30_000;

// Every control in a card is at least 44 px (Apple HIG); fields use 16 px text
// (no zoom on focus) and a placeholder that meets 4.5:1 in both themes.
const CARD_BODY =
  'space-y-3 px-3 py-3 [&_button]:min-h-[44px] [&_select]:min-h-[44px] [&_textarea]:min-h-[44px] [&_input:not([type=checkbox]):not([type=radio])]:min-h-[44px] [&_input]:text-base [&_select]:text-base [&_textarea]:text-base [&_li>label]:min-h-[44px] [&_a]:inline-flex [&_a]:min-h-[44px] [&_a]:items-center [&_::placeholder]:text-tcs-text-muted';

interface CardShellProps {
  title: string;
  icon: ReactNode;
  accentClass?: string; // border / left-stripe accent
  status: CopilotProposalStatus;
  committedLabel?: string;
  isCommitting?: boolean;
  // AFB-0056 — let a card block its own commit when a required field is empty,
  // rather than each card silently no-op'ing an enabled button.
  doItDisabled?: boolean;
  doItDisabledReason?: string;
  onDoIt: () => void;
  onDiscard: () => void;
  children: ReactNode;
  /** What to do next once the save is done (e.g. send the application). Stays
   *  live after commit, when the fields above it are locked. */
  after?: ReactNode;
  /** On a card that moves money, sends something out or cannot be undone: the
   *  who / how much / what, said in words above the button (TCSD-0533, D3 B). */
  readBack?: ReactNode;
  /** The button's words when they name the consequence ("Pay HD Services $1,240"). */
  doItLabel?: string;
}

export function CardShell({
  title,
  icon,
  accentClass = 'border-tcs-border',
  status,
  committedLabel,
  isCommitting,
  doItDisabled,
  doItDisabledReason,
  onDoIt,
  onDiscard,
  children,
  after,
  readBack,
  doItLabel,
}: CardShellProps) {
  const isCommitted =
    status === 'Committed' || status === 'Edited_And_Committed';
  const isDiscarded = status === 'Discarded';

  const [collapsed, setCollapsed] = useState(false);

  // Auto-collapse 30s after the card transitions to a committed state.
  // Manual expand resets if the user clicks the chevron.
  useEffect(() => {
    if (!isCommitted) {
      setCollapsed(false);
      return;
    }
    const t = setTimeout(() => setCollapsed(true), COLLAPSE_DELAY_MS);
    return () => clearTimeout(t);
  }, [isCommitted]);

  if (isCommitted && collapsed) {
    return (
      <div
        className={`rounded-lg border ${accentClass} bg-tcs-raised shadow-sm opacity-75`}
      >
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="flex min-h-[44px] w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm"
        >
          <span className="flex items-center gap-2 text-tcs-text-secondary">
            <span className="text-st-ok-fg">
              <Check className="h-4 w-4" />
            </span>
            <span className="text-tcs-text-muted">{icon}</span>
            <span className="font-medium text-tcs-text">{title}</span>
            <span className="text-tcs-text-muted">·</span>
            <span className="text-xs text-tcs-text-muted">
              {committedLabel ?? 'Saved'}
            </span>
          </span>
          <span className="flex items-center gap-1 text-xs text-tcs-text-muted">
            Expand
            <ChevronDown className="h-3.5 w-3.5" />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border ${accentClass} bg-tcs-raised shadow-sm transition-opacity ${
        isCommitted ? 'opacity-75' : ''
      } ${isDiscarded ? 'opacity-40' : ''}`}
    >
      <div className="flex items-center justify-between border-b border-tcs-border px-3 py-2">
        <div className="flex items-center gap-2 text-sm font-medium text-tcs-text">
          <span className="text-tcs-text-muted">{icon}</span>
          <span>{title}</span>
        </div>
        {isCommitted && (
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="-my-2 flex min-h-[44px] items-center gap-1 px-2 text-sm text-tcs-text-muted hover:text-tcs-text"
            title="Collapse to one-line summary"
          >
            Collapse
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <fieldset
        disabled={isCommitted || isDiscarded}
        className={`${CARD_BODY} m-0 min-w-0 border-0`}
      >
        {children}
      </fieldset>
      {after && <div className={CARD_BODY}>{after}</div>}
      {readBack && !isCommitted && !isDiscarded && (
        <p
          data-testid="card-read-back"
          className="mx-3 mb-2 rounded-md bg-st-warn-bg px-3 py-2 text-sm text-tcs-text"
        >
          {readBack}
        </p>
      )}

      <div className="sticky bottom-0 rounded-b-lg border-t border-tcs-border bg-tcs-raised px-3 py-2">
        {isCommitted ? (
          <div className="flex items-center justify-center gap-2 text-sm font-medium text-st-ok-fg">
            <Check className="h-4 w-4" />
            <span>{committedLabel ?? 'Saved'}</span>
          </div>
        ) : isDiscarded ? (
          <div className="text-center text-xs text-tcs-text-muted">Discarded</div>
        ) : (
          <>
            <button
              type="button"
              onClick={onDoIt}
              disabled={!!isCommitting || !!doItDisabled}
              className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md bg-tcs-inverse px-3 py-2 text-base font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-60"
            >
              {isCommitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <span>{doItLabel ?? 'Do it'}</span>
                  <span>→</span>
                </>
              )}
            </button>
            {doItDisabled && doItDisabledReason && (
              <p className="mt-1.5 text-center text-xs text-st-danger-fg">
                {doItDisabledReason}
              </p>
            )}
            <button
              type="button"
              onClick={onDiscard}
              disabled={!!isCommitting}
              className="mt-2 flex min-h-[44px] w-full items-center justify-center rounded-md border border-tcs-border bg-transparent px-3 py-2 text-base text-tcs-text hover:bg-tcs-bg-muted disabled:opacity-60"
            >
              Discard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
